import { useState } from 'react';

import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useParams } from 'react-router-dom';

import { useGetFailedDocs } from '@/api/queries/useGetFailedDocs';
import { useGetMatterRuns } from '@/api/queries/useGetMatterRuns';
import { FailedDocument } from '@/api/types/backend-dto-types';
import formatDate from '@/helpers/formatDate';
import { formatFileSize } from '@/new/components/file-uploader/utils';
import { ScrollableTable } from '@/new/components/table/scrollable-table';

const fallbackTableData: FailedDocument[] = [];
const columnHelper = createColumnHelper<FailedDocument>();

const columns = [
  columnHelper.accessor('name', {
    header: () => <span>Name</span>,
    cell: (info) => <span className="text-gray-900">{info.getValue()}</span>,
  }),
  columnHelper.accessor('type', {
    header: () => <span>Type</span>,
    cell: (info) => <span className="text-gray-500">{info.getValue() || '—'}</span>,
  }),
  columnHelper.accessor('size', {
    header: () => <span>Size</span>,
    cell: (info) => <span className="text-gray-500">{formatFileSize(info.getValue() ?? 0)}</span>,
  }),
  columnHelper.accessor('uploadDate', {
    header: () => <span>Upload Date</span>,
    cell: (info) => <span className="text-gray-500">{formatDate(info.getValue(), true, true)}</span>,
  }),
  columnHelper.accessor('pipelineErrorType', {
    header: () => <span>Error Reason</span>,
    cell: (info) => <span className="text-red-600">{info.getValue()}</span>,
  }),
];

export function FailedDocuments() {
  const { matterId, runId } = useParams();
  if (!matterId || !runId) return null;

  const [globalFilter, setGlobalFilter] = useState('');
  const { data: runs } = useGetMatterRuns(matterId);
  const { data: failedDocs, isLoading: isLoadingFailedDocs } = useGetFailedDocs(runId);

  const selectedRun = runs?.find((r) => r.id === runId);

  const table = useReactTable({
    data: failedDocs ?? fallbackTableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      globalFilter,
    },
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  return (
    <div className="flex flex-col">
      <div className="mb-4 flex items-center justify-between gap-2">
        <div>
          <div className="mb-4 flex items-center justify-between gap-2"></div>
          <h2 className="mb-1 text-base font-semibold">Failed Documents</h2>
          <p className="text-sm text-gray-600">
            {selectedRun
              ? `Documents that failed to process in the run started ${formatDate(selectedRun.createdDate, true, true)}`
              : 'Documents that failed to process'}
          </p>
        </div>
      </div>

      <ScrollableTable
        table={table}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        plural="failed documents"
        loading={isLoadingFailedDocs}
        className="p-0"
      />
    </div>
  );
}
